import { useMemo } from 'react';
import { AssetPlanDashboardStatsInput } from '#/graphql/types';
import useAssetPlanDashboardStatsQuery from './useAssetPlanDashboardStatsQuery';
import { Item, ItemKeys } from './useAssetPlanDashboardItems';

type AssetPlanStatsKey = keyof AssetPlanDashboardStatsInput;

const map: Record<ItemKeys, AssetPlanStatsKey[]> = {
  'general-metrics': ['metrics'],
  'intervention-cost-by-priority': ['interventionCostByPriority'],
  'asset-metrics': ['assetMetrics'],
  'findings-metrics': ['findingsMetrics'],
  'intervention-cost-by-age': ['interventionCostByAge'],
  'replacement-cost-by-asset-type': ['replacementCostByAssetType'],
  'replacement-cost-by-manufacturer': ['replacementCostByManufacturer'],
  'intervention-cost-and-fci': ['interventionCostAndFci'],
};

const useAssetPlanDashboardSingleStatsQuery = (
  key: ItemKeys,
  visibleItems: Item[],
  assetPlanId: string,
  scenarioId: string,
) => {
  const statKeys = map[key];
  const skip = !assetPlanId || !scenarioId || visibleItems.every((item) => item.key !== key);

  const stats: AssetPlanDashboardStatsInput = useMemo(() => {
    return statKeys.reduce(
      (res, statKey) => ({
        ...res,
        [statKey]: true,
      }),
      {},
    );
  }, [key, ...statKeys]);

  const response = useAssetPlanDashboardStatsQuery(stats, assetPlanId, scenarioId, { skip });

  const data = useMemo(() => {
    const statsData = response.data?.getAssetPlanDashboardStats?.data;
    if (!statsData) return null;

    return statsData[statKeys[0] as keyof typeof statsData] ?? null;
  }, [response.data?.getAssetPlanDashboardStats?.data, statKeys]);

  return {
    loading: response.loading,
    data,
  };
};

export default useAssetPlanDashboardSingleStatsQuery;
